/**
 * Plantillas de WhatsApp para expedientes AR.
 *
 * Meta exige que todo mensaje que ABRE conversación —el deudor no ha escrito en
 * 24h— sea una plantilla revisada y aprobada por ellos de antemano. No se puede
 * redactar en el momento, ni por una persona ni por la IA.
 *
 * ── Por qué la elige el código y no la IA ───────────────────────────────────
 * Cuál toca es una TABLA, no un juicio: depende del número de intento y de si el
 * deudor respondió alguna vez. Seis casillas. Dejarlo en manos del modelo
 * añadiría no-determinismo y coste de tokens a una decisión que no los necesita,
 * y haría imposible auditar por qué se mandó lo que se mandó.
 *
 * ── Estado ──────────────────────────────────────────────────────────────────
 * Los textos de aquí son BORRADORES para poder construir y probar el mecanismo.
 * Los definitivos son decisión de producto y además los revisa Meta, así que
 * hay que sustituirlos antes de registrar nada.
 */

/**
 * Claves de la librería estándar. Son NUESTRAS: el nombre con el que Meta las
 * conoce se guarda aparte, porque cada company las registra en su propia cuenta.
 */
const AR_TEMPLATE_KEYS = {
  PRIMER_CONTACTO: 'ar_primer_contacto',
  SEGUNDO_AVISO: 'ar_segundo_aviso',
  TERCER_AVISO: 'ar_tercer_aviso',
};

/**
 * La escalera de contacto en frío. Tres intentos y se acaba.
 *
 * Hoy las dos ramas —el deudor nunca respondió, o respondió y se enfrió—
 * comparten las mismas tres plantillas. Se modela igualmente por separado
 * porque son situaciones distintas y es previsible que acaben teniendo textos
 * propios; cuando ocurra, solo cambia esta tabla.
 */
const AR_ESCALATION = {
  /** El deudor no ha contestado nunca. */
  never_responded: [
    AR_TEMPLATE_KEYS.PRIMER_CONTACTO,
    AR_TEMPLATE_KEYS.SEGUNDO_AVISO,
    AR_TEMPLATE_KEYS.TERCER_AVISO,
  ],
  /** Respondió en algún momento, y ahora lleva más de 24h en silencio. */
  reengagement: [
    AR_TEMPLATE_KEYS.PRIMER_CONTACTO,
    AR_TEMPLATE_KEYS.SEGUNDO_AVISO,
    AR_TEMPLATE_KEYS.TERCER_AVISO,
  ],
};

/** Tope de intentos en frío del flujo automático. */
const AR_MAX_COLD_ATTEMPTS = AR_ESCALATION.never_responded.length;

/**
 * Estado de una plantilla en la cuenta de Meta de una company.
 *
 * Cuatro de los cinco son **de Meta**, con su mismo nombre en minúsculas, para
 * no tener que traducir en cada sincronización:
 *
 *   pending   — enviada, esperando revisión (hasta 24h; más en cuentas nuevas)
 *   approved  — aprobada: ya se puede enviar con ella
 *   rejected  — Meta la ha rechazado (el motivo se guarda aparte)
 *   disabled  — Meta la ha deshabilitado
 *
 * Los otros tres son **nuestros**, y no existen para Meta:
 *
 *   draft     — solo existe en ClaimUp; Meta ni la ha visto
 *   proposed  — la ha propuesto el cliente y espera a que ClaimUp la revise
 *   declined  — ClaimUp la ha devuelto al cliente, con un motivo
 *
 * Ninguno de los tres se envía nunca: solo `approved`. Ver
 * docs/ar-plantillas-propuestas-plan.md.
 */
const META_TEMPLATE_STATUSES = ['pending', 'approved', 'rejected', 'disabled'];
const CLIENT_PROPOSAL_STATUSES = ['proposed', 'declined'];
const TEMPLATE_APPROVAL_STATUSES = ['draft', ...CLIENT_PROPOSAL_STATUSES, ...META_TEMPLATE_STATUSES];
const DEFAULT_TEMPLATE_APPROVAL_STATUS = 'draft';

const TEMPLATE_APPROVAL_LABELS = {
  draft: 'Borrador',
  proposed: 'Propuesta, pendiente de ClaimUp',
  declined: 'Devuelta por ClaimUp',
  pending: 'En revisión por Meta',
  approved: 'Aprobada',
  rejected: 'Rechazada',
  disabled: 'Deshabilitada por Meta',
};

/**
 * Traduce el estado que devuelve Meta (en mayúsculas) al nuestro.
 * Un estado que no conozcamos NO se convierte en `approved` por descuido: se
 * deja como `disabled`, que es la lectura conservadora — no se puede enviar.
 */
function fromMetaStatus(status) {
  const s = String(status || '').toLowerCase();
  return META_TEMPLATE_STATUSES.includes(s) ? s : 'disabled';
}

/**
 * Categoría de Meta. `UTILITY` es la que corresponde a un aviso sobre una
 * transacción existente (una factura pendiente). `MARKETING` tiene otras reglas
 * y otro precio, y no es lo que hacemos.
 */
const TEMPLATE_CATEGORY = 'UTILITY';
const TEMPLATE_LANGUAGE = 'es_ES';

/**
 * Borradores de la librería estándar. **Textos provisionales.**
 *
 * Las variables van con la sintaxis de Meta (`{{nombre}}`) y con parámetros CON
 * NOMBRE, no posicionales: así un cambio en el orden no manda el importe donde
 * iba el nombre. Cada una necesita un ejemplo — Meta rechaza la plantilla si no
 * se lo das.
 */
// El `escalation_order` de cada borrador define el recorrido POR DEFECTO del
// catálogo: al añadir una al cliente se lleva su peldaño, y desde ahí un admin
// lo cambia si ese cliente necesita otro orden.
const AR_TEMPLATE_DRAFTS = [
  {
    key: AR_TEMPLATE_KEYS.PRIMER_CONTACTO,
    escalation_order: 1,
    description: 'Primer contacto en frío sobre una deuda pendiente.',
    body_text:
      'Hola {{customer_name}}, le escribimos de {{company_name}}. Nos consta pendiente de pago {{debt_title}}, por importe de {{amount}}. Si ya lo ha abonado o hay algo que revisar, respóndanos a este mensaje y lo vemos.',
    variables: [
      { name: 'customer_name', example: 'Jessica' },
      { name: 'company_name', example: 'Acme Industries' },
      { name: 'debt_title', example: 'FE23077567' },
      { name: 'amount', example: '300,00 €' },
    ],
  },
  {
    key: AR_TEMPLATE_KEYS.SEGUNDO_AVISO,
    escalation_order: 2,
    description: 'Segundo intento, una semana después del primero.',
    body_text:
      'Hola {{customer_name}}, volvemos a escribirle desde {{company_name}} por {{debt_title}}, que sigue pendiente. Si necesita el detalle o quiere plantear una forma de pago, respóndanos y lo gestionamos.',
    variables: [
      { name: 'customer_name', example: 'Jessica' },
      { name: 'company_name', example: 'Acme Industries' },
      { name: 'debt_title', example: 'FE23077567' },
    ],
  },
  {
    key: AR_TEMPLATE_KEYS.TERCER_AVISO,
    escalation_order: 3,
    description: 'Último intento antes de cerrar el circuito automático.',
    body_text:
      'Hola {{customer_name}}, este es nuestro último aviso desde {{company_name}} sobre {{debt_title}}. Si no recibimos respuesta, el expediente seguirá su curso. Puede contestarnos aquí en cualquier momento.',
    variables: [
      { name: 'customer_name', example: 'Jessica' },
      { name: 'company_name', example: 'Acme Industries' },
      { name: 'debt_title', example: 'FE23077567' },
    ],
  },
];

module.exports = {
  AR_TEMPLATE_KEYS,
  AR_ESCALATION,
  AR_MAX_COLD_ATTEMPTS,
  META_TEMPLATE_STATUSES,
  CLIENT_PROPOSAL_STATUSES,
  fromMetaStatus,
  TEMPLATE_APPROVAL_STATUSES,
  DEFAULT_TEMPLATE_APPROVAL_STATUS,
  TEMPLATE_APPROVAL_LABELS,
  TEMPLATE_CATEGORY,
  TEMPLATE_LANGUAGE,
  AR_TEMPLATE_DRAFTS,
};
